import React, { useState, useEffect } from "react";
import { X, Star } from "lucide-react";
import "./UserRoleModal.css";
import MultiCombobox from "../../../components/MultiCombobox/MultiCombobox";
import Combobox from "../../../components/Combobox/Combobox";
import ButtonDelete from "../../../components/Buttons/ButtonDelete/ButtonDelete";
import ButtonCancel from "../../../components/Buttons/ButtonCancel/ButtonCancel";
import ButtonSave from "../../../components/Buttons/ButtonSave/ButtonSave";
import { useTranslation } from "react-i18next";
import customFetch from "../../../../helpers/axios";
import { normalizeApiResponse } from "../../../../helpers/apiUtils";
import useUser from "../../../../hooks/useUser";

export default function UserRoleModal({ isOpen, onClose, role, onSave, onDelete }) {
  const { t } = useTranslation('userRoles');
  const { user: userCtx } = useUser();
  const currentUserOrganization = userCtx?.organization || userCtx?.organization_id;
  
  const [formData, setFormData] = useState({
    name: "",
    credit_limit: "",
    allowed_llms: [],
    default_llm: "",
    have_admin_access: false,
    knowledge_bases: [],
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [llmOptions, setLlmOptions] = useState([]); // [{id, name}]
  const [knowledgeBaseOptions, setKnowledgeBaseOptions] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchLlmsAndInit = async () => {
      try {
        const [res, kbRes] = await Promise.all([
          customFetch.get("/api/organizations/organization/agents/"),
          customFetch.get("/api/knowledge-bases/", { params: { is_active: true } }),
        ]);
        const normalized = normalizeApiResponse(res.data);
        const items = (normalized && (normalized.results || normalized)) || [];
        const options = Array.isArray(items)
          ? items
              .map((it) => ({ id: it?.id ?? it?.model_id, name: it?.name ?? it?.model_name ?? it?.display_name ?? it?.title }))
              .filter((o) => o.id !== undefined && o.id !== null && o.id !== '' && !!o.name)
          : [];
        const normalizedKnowledgeBases = normalizeApiResponse(kbRes.data);
        const kbItems = (normalizedKnowledgeBases && (normalizedKnowledgeBases.results || normalizedKnowledgeBases)) || [];
        const kbOptions = Array.isArray(kbItems)
          ? kbItems
              .map((item) => ({ id: item?.id, name: item?.name }))
              .filter((item) => item.id !== undefined && item.id !== null && !!item.name)
          : [];
        if (!cancelled) {
          setLlmOptions(options);
          setKnowledgeBaseOptions(kbOptions);
        }

        // Normalize helper for fuzzy name matching
        const norm = (s) =>
          String(s || "")
            .toLowerCase()
            .replace(/\s*\([^)]*\)\s*$/, "") // remove provider suffix in parentheses
            .trim();

        const toIdArray = (arr) => {
          if (!Array.isArray(arr) || arr.length === 0) return [];
          const idSet = new Set(options.map((o) => String(o.id)));
          const mapByName = new Map(options.map((o) => [norm(o.name), String(o.id)]));
          return arr
            .map((item) => {
              // number id
              if (typeof item === 'number') return String(item);
              // string: could be id or name
              if (typeof item === 'string') {
                if (idSet.has(item)) return item;
                const byName = mapByName.get(norm(item));
                if (byName) return byName;
                // fallback: partial includes
                const hit = options.find((o) => norm(o.name).includes(norm(item)));
                return hit ? String(hit.id) : null;
              }
              // object: may have id/model_id/name
              if (item && typeof item === 'object') {
                const id = item.id ?? item.model_id;
                if (id != null) return String(id);
                const byName = mapByName.get(norm(item.name ?? item.model_name ?? item.display_name));
                if (byName) return byName;
                const hit = options.find((o) => norm(o.name).includes(norm(item.name)));
                return hit ? String(hit.id) : null;
              }
              return null;
            })
            .filter(Boolean);
        };

        const toId = (val) => {
          if (!val) return "";
          const idSet = new Set(options.map((o) => String(o.id)));
          if (typeof val === 'number') return String(val);
          if (typeof val === 'object') {
            const id = val?.id ?? val?.model_id;
            if (id != null) return String(id);
            const candidateName = val?.name ?? val?.model_name ?? val?.display_name ?? val?.title;
            const exact = options.find((o) => norm(o.name) === norm(candidateName));
            if (exact) return String(exact.id);
            const partial = options.find((o) => norm(o.name).includes(norm(candidateName)));
            return partial ? String(partial.id) : "";
          }
          if (typeof val === 'string') {
            if (idSet.has(val)) return val; // already an id
            const exact = options.find((o) => norm(o.name) === norm(val));
            if (exact) return String(exact.id);
            const partial = options.find((o) => norm(o.name).includes(norm(val)));
            return partial ? String(partial.id) : "";
          }
          return "";
        };

        if (!cancelled) {
          if (role) {
            const allowedIds = toIdArray(role.allowed_llms);
            let defaultId = toId(role.default_llm);
            // Ensure default is among allowed
            if (defaultId && !allowedIds.includes(defaultId)) {
              defaultId = "";
            }
            setFormData({
              name: role.name || "",
              credit_limit: (role.credit_limit ?? "").toString(),
              allowed_llms: allowedIds,
              default_llm: defaultId,
              have_admin_access: role.have_admin_access || false,
              knowledge_bases: Array.isArray(role.knowledge_bases)
                ? role.knowledge_bases.map((kb) => String(kb.id))
                : [],
            });
          } else {
            setFormData({
              name: "",
              credit_limit: "",
              allowed_llms: [],
              default_llm: "",
              have_admin_access: false,
              knowledge_bases: [],
            });
          }
          setErrors({});
          setShowDeleteConfirm(false);
        }
      } catch (e) {
        if (!cancelled) {
          setLlmOptions([]);
          setKnowledgeBaseOptions([]);
          // Still initialize form without LLMs
          if (role) {
            setFormData({
              name: role.name || "",
              credit_limit: (role.credit_limit ?? "").toString(),
              allowed_llms: [],
              default_llm: "",
              have_admin_access: role.have_admin_access || false,
              knowledge_bases: Array.isArray(role.knowledge_bases)
                ? role.knowledge_bases.map((kb) => String(kb.id))
                : [],
            });
          } else {
            setFormData({ name: "", credit_limit: "", allowed_llms: [], default_llm: "", have_admin_access: false, knowledge_bases: [] });
          }
          setErrors({});
          setShowDeleteConfirm(false);
        }
      }
    };

    fetchLlmsAndInit();
    return () => {
      cancelled = true;
    };
  }, [isOpen, role]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('modal.errors.nameRequired');
    } else if (formData.name.length > 50) {
      newErrors.name = t('modal.errors.nameMax');
    }

    // Credit limit is optional: empty => Unlimited (null)
    if (formData.credit_limit.trim() !== "") {
      const creditLimit = parseInt(formData.credit_limit, 10);
      if (isNaN(creditLimit) || creditLimit < 0) {
        newErrors.credit_limit = t('modal.errors.creditPositive');
      }
    }

    // If default_llm is set and allowed_llms provided, ensure membership
    if (formData.default_llm && Array.isArray(formData.allowed_llms) && formData.allowed_llms.length > 0) {
      if (!formData.allowed_llms.includes(formData.default_llm)) {
        newErrors.default_llm = t('modal.errors.defaultLLMInAllowed');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        credit_limit: formData.credit_limit.trim() === "" ? null : parseInt(formData.credit_limit, 10),
        organization: currentUserOrganization?.id || currentUserOrganization || null,
        // Send ids array (numbers)
        allowed_llms: Array.isArray(formData.allowed_llms) ? formData.allowed_llms : [],
        // Send id (number) or null
        default_llm: formData.default_llm || null,
        have_admin_access: formData.have_admin_access,
        knowledge_bases: Array.isArray(formData.knowledge_bases) ? formData.knowledge_bases : [],
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      // Handle API errors
      if (error.response?.data) {
        const apiErrors = {};
        Object.keys(error.response.data).forEach((key) => {
          const val = error.response.data[key];
          if (Array.isArray(val)) {
            apiErrors[key] = val[0];
          } else if (typeof val === 'string') {
            apiErrors[key] = val;
          } else if (val && typeof val === 'object') {
            apiErrors[key] = JSON.stringify(val);
          }
        });
        setErrors(apiErrors);
      } else {
        setErrors({
          general: t('modal.errors.general')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!role) return;
    
    setIsLoading(true);
    try {
      await onDelete(role.id);
      onClose();
    } catch (error) {
      setErrors({ general: t('modal.errors.delete') });
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-role-modal-overlay">
      <div className="user-role-modal">
        <div className="user-role-modal-header">
          <div className="user-role-modal-title-section">
            <Star className="user-role-modal-icon" size={20} />
            <h2 className="user-role-modal-title">
              {role ? t('modal.titleEdit') : t('modal.titleCreate')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="user-role-modal-close-btn"
            disabled={isLoading}
            aria-label={t('modal.aria.close')}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-role-modal-form">
          {errors.general && (
            <div className="user-role-modal-error-general">
              {errors.general}
            </div>
          )}

          {/* Code is system-managed: hidden and non-editable */}

          <div className="user-role-modal-field">
            <label htmlFor="name" className="user-role-modal-label">
              {t('modal.fields.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`user-role-modal-input ${errors.name ? "error" : ""}`}
              placeholder={t('modal.fields.namePlaceholder')}
              disabled={isLoading}
              maxLength={50}
            />
            {errors.name && (
              <span className="user-role-modal-error">{errors.name}</span>
            )}
          </div>

          <div className="user-role-modal-field">
            <label htmlFor="organization" className="user-role-modal-label">
              {t('modal.fields.organization')}
            </label>
            <input
              type="text"
              id="organization"
              value={currentUserOrganization?.name || "Aulasneo"}
              className="user-role-modal-input"
              disabled
              readOnly
            />
          </div>

          <div className="user-role-modal-field">
            <label htmlFor="credit_limit" className="user-role-modal-label">
              {t('modal.fields.creditLimit')}
            </label>
            <input
              type="number"
              id="credit_limit"
              name="credit_limit"
              value={formData.credit_limit}
              onChange={handleInputChange}
              className={`user-role-modal-input ${errors.credit_limit ? "error" : ""}`}
              placeholder={t('modal.fields.creditPlaceholder')}
              disabled={isLoading}
              min="0"
            />
            {errors.credit_limit && (
              <span className="user-role-modal-error">{errors.credit_limit}</span>
            )}
          </div>

          <div className="user-role-modal-field">
            <label className="user-role-modal-checkbox-label">
              <input
                type="checkbox"
                name="have_admin_access"
                checked={formData.have_admin_access}
                onChange={handleInputChange}
                disabled={isLoading}
                className="user-role-modal-checkbox"
              />
              <span className="user-role-modal-checkbox-text">
                {t('modal.fields.haveAdminAccess')}
              </span>
            </label>
            {errors.have_admin_access && (
              <span className="user-role-modal-error">{errors.have_admin_access}</span>
            )}
          </div>

          {/* Allowed LLMs - dropdown multi-select with checkboxes */}
          <div className="user-role-modal-field">
            <label htmlFor="allowed_llms" className="user-role-modal-label">
              {t('modal.fields.allowedLLMs')}
            </label>
            <div className="user-role-modal-checkbox-row">
              <label className="user-role-modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={
                    Array.isArray(formData.allowed_llms) &&
                    formData.allowed_llms.length > 0 &&
                    formData.allowed_llms.length === llmOptions.length
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData((prev) => {
                      if (!checked) {
                        return { ...prev, allowed_llms: [], default_llm: "" };
                      }
                      const allIds = llmOptions.map((o) => String(o.id));
                      const next = { ...prev, allowed_llms: allIds };
                      if (next.default_llm && !allIds.includes(String(next.default_llm))) {
                        next.default_llm = "";
                      }
                      return next;
                    });
                    if (errors.allowed_llms) {
                      setErrors((prev) => ({ ...prev, allowed_llms: "" }));
                    }
                  }}
                  disabled={isLoading || llmOptions.length === 0}
                  className="user-role-modal-checkbox"
                />
                <span className="user-role-modal-checkbox-text">
                  {t('modal.fields.selectAllAgents')}
                </span>
              </label>
            </div>
            <MultiCombobox
              id="allowed_llms"
              values={(formData.allowed_llms || []).map(String)}
              options={llmOptions.map((o) => ({ value: String(o.id), label: o.name }))}
              onChange={(vals) => {
                const selectedIds = Array.isArray(vals) ? vals.map(String) : [];
                setFormData((prev) => {
                  const next = { ...prev, allowed_llms: selectedIds };
                  if (next.default_llm && !selectedIds.includes(String(next.default_llm))) {
                    next.default_llm = "";
                  }
                  return next;
                });
                if (errors.allowed_llms) setErrors((prev) => ({ ...prev, allowed_llms: "" }));
              }}
              label={t('modal.fields.allowedLLMs')}
              className="combobox-full"
            />
            {errors.allowed_llms && (
              <span className="user-role-modal-error">{errors.allowed_llms}</span>
            )}
          </div>

          {/* Default LLM - select from allowed */}
          <div className="user-role-modal-field">
            <label htmlFor="default_llm" className="user-role-modal-label">
              {t('modal.fields.defaultLLM')}
            </label>
            <Combobox
              id="default_llm"
              value={String(formData.default_llm || "")}
              options={(llmOptions.filter((o) => (formData.allowed_llms || []).map(String).includes(String(o.id))) || [])
                .map((o) => ({ value: String(o.id), label: o.name }))}
              onChange={(val) => {
                const value = String(val || "");
                setFormData((prev) => ({ ...prev, default_llm: value }));
                if (errors.default_llm) setErrors((prev) => ({ ...prev, default_llm: "" }));
              }}
              label={t('modal.fields.selectDefaultLLM')}
              className="combobox-full"
            />
            {errors.default_llm && (
              <span className="user-role-modal-error">{errors.default_llm}</span>
            )}
          </div>

          <div className="user-role-modal-field">
            <label htmlFor="knowledge_bases" className="user-role-modal-label">
              Knowledge Bases
            </label>
            <MultiCombobox
              id="knowledge_bases"
              values={(formData.knowledge_bases || []).map(String)}
              options={knowledgeBaseOptions.map((item) => ({ value: String(item.id), label: item.name }))}
              onChange={(vals) => {
                setFormData((prev) => ({ ...prev, knowledge_bases: Array.isArray(vals) ? vals.map(String) : [] }));
                if (errors.knowledge_bases) setErrors((prev) => ({ ...prev, knowledge_bases: "" }));
              }}
              label="Knowledge Bases"
              placeholder="Select knowledge bases..."
              className="combobox-full"
            />
            {errors.knowledge_bases && (
              <span className="user-role-modal-error">{errors.knowledge_bases}</span>
            )}
          </div>

          <div className="user-role-modal-actions">
            {role && (
              <div className="user-role-modal-actions-left">
                <ButtonDelete
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isLoading}
                  ariaLabel={t('modal.aria.deleteRole')}
                />
              </div>
            )}
            
            <div className="user-role-modal-actions-right">
              <ButtonCancel onClick={handleClose} disabled={isLoading} />
              <ButtonSave
                type="submit"
                isEdit={!!role}
                isLoading={isLoading}
                disabled={isLoading}
              />
            </div>
          </div>
        </form>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="user-role-confirm-overlay">
            <div className="user-role-confirm-modal">
              <h3 className="user-role-confirm-title">{t('modal.delete.title')}</h3>
              <p className="user-role-confirm-message">
                {t('modal.delete.message', { name: role?.name })}
              </p>
              <div className="user-role-confirm-actions">
                <ButtonCancel
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isLoading}
                />
                <ButtonDelete
                  onClick={handleDelete}
                  disabled={isLoading}
                  ariaLabel={t('modal.aria.confirmDeleteRole')}
                >
                  {isLoading ? t('modal.delete.deleting') : t('common:buttons.delete')}
                </ButtonDelete>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
